import { z } from 'zod';
import { ViewportPresetIdSchema } from './viewport-presets';
import { LocaleIdSchema } from './locales';
import { REVENUE_TIERS } from './revenue-weighted-coverage';

/**
 * Schemas for flow CRUD endpoints. Mirrors the existing Zod schemas
 * in packages/api/src/schemas/flows.ts; consolidated here so frontend
 * apps can `safeParse` the same shape they send.
 *
 * Note: the editor-side `TestFlow` type in flow-builder is a richer
 * client-side shape (with parsed `steps`, `tags`, etc.) that's
 * independent of the API contract — the API still stores the editor
 * model as opaque JSON in the `canvas_json` column.
 */

// ---- Lease / pending-review summaries embedded in FlowDetail ----
//
// These are intentionally light shapes (just enough for the editor's
// header banner / status pill). Full FlowLease + FlowReview shapes
// live in flow-leases.ts / flow-reviews.ts; the embedded summaries
// stay in this file so /api/flows/:id callers can render the editor
// chrome without a second round-trip.

export const FlowLeaseSummarySchema = z.object({
  holderId: z.string().uuid(),
  holderName: z.string(),
  acquiredAt: z.string(),
  lastHeartbeatAt: z.string(),
  expiresAt: z.string(),
});
export type FlowLeaseSummary = z.infer<typeof FlowLeaseSummarySchema>;

export const FlowPendingReviewSummarySchema = z.object({
  id: z.string().uuid(),
  requestedBy: z.string().uuid(),
  requestedByName: z.string(),
  draftVersionAtRequest: z.number().int(),
  approvalsCount: z.number().int(),
  changesRequestedCount: z.number().int(),
  createdAt: z.string(),
});
export type FlowPendingReviewSummary = z.infer<typeof FlowPendingReviewSummarySchema>;

/**
 * Scenario → Automation lineage (Phase 1). Canonical source is the
 * `quality_scenario_flows` join — not tags, not canvas metadata, not
 * name matching. Many-to-many: a flow may link to multiple scenarios.
 */
export const FlowScenarioLineageEntrySchema = z.object({
  scenarioId: z.string().uuid(),
  title: z.string(),
  domainName: z.string().nullable().optional(),
  moduleName: z.string().nullable().optional(),
  businessFlowName: z.string().nullable().optional(),
});
export type FlowScenarioLineageEntry = z.infer<typeof FlowScenarioLineageEntrySchema>;

export const FlowSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  project_id: z.string().nullable(),
  folder_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  // Versioning fields land on the summary too so list views can show
  // a "v3 · Draft v8" badge without a per-row detail fetch.
  draft_version: z.number().int().optional(),
  published_version: z.number().int().optional(),
  published_at: z.string().nullable().optional(),
  // Migration 083: coarse `desktop` / `tablet` / `mobile` segment
  // the run-config drawer pre-selects. NULL = launcher falls back
  // to `desktop` (historical implicit default).
  default_viewport_preset_id: ViewportPresetIdSchema.nullable().optional(),
  // Migration 084: per-flow locale id (BCP-47, see
  // `@rynora/contracts/locales`). NULL = runner falls back to
  // the global config locale (today's `en-US` default).
  default_locale_id: LocaleIdSchema.nullable().optional(),
  // Migration 120 (`pending-revenue-weighted-coverage` v0): operator
  // tag that weights the flow in release-readiness + PR-comment
  // revenue-weighted coverage tiles. NULL = "untagged" — explicitly
  // excluded from the weighted denominator (locked decision §3) so
  // an unset flow doesn't drag the score down.
  revenue_tier: z.enum(REVENUE_TIERS).nullable().optional(),
  /**
   * Scenario lineage from `quality_scenario_flows` (canonical join).
   * Empty / omitted when the flow has no Scenario link. Never derived
   * from tags or name matching.
   */
  scenario_lineage: z.array(FlowScenarioLineageEntrySchema).optional(),
});
export type FlowSummary = z.infer<typeof FlowSummarySchema>;

export const FlowDetailSchema = FlowSummarySchema.extend({
  // The published snapshot. Scheduled / CI runs read this. Unchanged
  // from the pre-migration shape so existing readers stay correct.
  canvas_json: z.record(z.unknown()),
  // The in-progress draft. The editor reads/writes this. May equal
  // `canvas_json` when nothing has been edited since the last publish.
  draft_canvas_json: z.record(z.unknown()).optional(),
  draft_version: z.number().int(),
  published_version: z.number().int(),
  published_at: z.string().nullable(),
  published_by: z.string().uuid().nullable(),
  // Null when no other user holds the lease right now. When non-null
  // and `holderId !== currentUser.id`, the editor renders a view-only
  // banner with "Take over" CTA.
  lease: FlowLeaseSummarySchema.nullable(),
  // Null when no pending review exists. Drives the status pill copy
  // ("Draft v8 - in review by Bob"). Approved reviews live in the
  // dedicated /reviews endpoint.
  pendingReview: FlowPendingReviewSummarySchema.nullable(),
});
export type FlowDetail = z.infer<typeof FlowDetailSchema>;

/**
 * The only values `test_cases.status` accepts (DB constraint
 * `valid_status`). Anything else used to reach Postgres and surface as a
 * 500 from `POST /api/flows` / `PUT /api/flows/:id`; the wire schema now
 * bounds it so an unknown value is a 400 instead.
 */
export const FLOW_STATUSES = ['NEW', 'DRAFT', 'SAVED', 'REOPENED'] as const;
export type FlowStatus = (typeof FLOW_STATUSES)[number];

/**
 * Case-insensitive on the wire, upper-case in the column. Clients that
 * send `draft` get the persisted `DRAFT` rather than a constraint
 * violation — the value was always legal, only the casing wasn't.
 */
export const FlowStatusSchema = z.preprocess(
  (value) => (typeof value === 'string' ? value.trim().toUpperCase() : value),
  z.enum(FLOW_STATUSES),
);

export const CreateFlowRequestSchema = z.object({
  name: z.string().max(255).optional(),
  project_id: z.string().uuid().optional(),
  folder_id: z.string().uuid().nullable().optional(),
  canvas_json: z.record(z.unknown()).optional(),
  status: FlowStatusSchema.optional(),
  // Migration 083: caller can pre-set the coarse viewport preset
  // straight from the new-flow wizard so the very first run picks
  // the right segment without a round-trip through the drawer.
  default_viewport_preset_id: ViewportPresetIdSchema.nullable().optional(),
  // Migration 084: caller can pre-set the locale id straight from
  // the wizard so the first run uses the right Playwright locale +
  // timezone + Accept-Language combo.
  default_locale_id: LocaleIdSchema.nullable().optional(),
});
export type CreateFlowRequest = z.infer<typeof CreateFlowRequestSchema>;

export const UpdateFlowRequestSchema = z.object({
  name: z.string().max(255).optional(),
  // Writes to `draft_canvas_json` after migration 046. The legacy
  // field name is kept on the wire so older clients keep working;
  // server still routes it to the draft column.
  canvas_json: z.record(z.unknown()).optional(),
  status: FlowStatusSchema.optional(),
  folder_id: z.string().uuid().nullable().optional(),
  // Optimistic-concurrency precondition. When supplied, the server
  // 409s with FLOW_DRAFT_VERSION_STALE if it doesn't match the row's
  // current draft_version. Optional for backward compatibility, but
  // the editor always sends it.
  expected_draft_version: z.number().int().optional(),
  // Lease ownership marker — passes through to assertCallerHoldsLease.
  holder_session_id: z.string().min(1).max(128).optional(),
  // Migration 047: target a specific branch. When omitted the
  // server resolves the user's owned branch (or the default branch
  // if they don't have one yet).
  branch_id: z.string().uuid().optional(),
  // Migration 083: rebind the coarse viewport preset. Sending
  // `null` clears it back to "no opinion" (launcher fallback).
  default_viewport_preset_id: ViewportPresetIdSchema.nullable().optional(),
  // Migration 084: rebind the locale id. Sending `null` clears it.
  default_locale_id: LocaleIdSchema.nullable().optional(),
  // Migration 120 (`pending-revenue-weighted-coverage` v0.1): operator
  // rebinds (or clears, with `null`) the revenue tier for this flow.
  // The flow-list dropdown PATCHes this; release-readiness +
  // PR-comment composers read it when computing the weighted tile.
  revenue_tier: z.enum(REVENUE_TIERS).nullable().optional(),
});
export type UpdateFlowRequest = z.infer<typeof UpdateFlowRequestSchema>;

export const ListFlowsResponseSchema = z.object({
  flows: z.array(FlowSummarySchema),
});
export type ListFlowsResponse = z.infer<typeof ListFlowsResponseSchema>;

export const FlowResponseSchema = z.object({
  flow: FlowDetailSchema,
});
export type FlowResponse = z.infer<typeof FlowResponseSchema>;

/** Phase 5D.3 — Flows that call this Flow via a call-flow step. */
export const FlowCallerSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string(),
  /** Canvas step id of the call-flow step when present. */
  stepId: z.string().nullable(),
  /** 0-based index in the caller's canvas steps when known. */
  stepIndex: z.number().int().nonnegative().nullable(),
  /** Deep link into the flow editor (`#/flow/:id`). */
  href: z.string().min(1),
});
export type FlowCaller = z.infer<typeof FlowCallerSchema>;

export const FlowCallersResponseSchema = z.object({
  projectId: z.string().uuid(),
  flowId: z.string().uuid(),
  flowName: z.string(),
  callers: z.array(FlowCallerSchema),
  callerCount: z.number().int().nonnegative(),
});
export type FlowCallersResponse = z.infer<typeof FlowCallersResponseSchema>;
